import React from "react";
import { Box, Select, MenuItem, IconButton, Typography } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

interface CustomPaginationProps {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
  onItemsPerPageChange: (itemsPerPage: number) => void;
}

export const CustomPagination: React.FC<CustomPaginationProps> = ({
  currentPage,
  totalPages,
  totalItems,
  itemsPerPage,
  onPageChange,
  onItemsPerPageChange,
}) => {
  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  const handlePrevious = () => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1);
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      onPageChange(currentPage + 1);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "flex-end",
        alignItems: "center",
        gap: 3,
        padding: "16px 24px",
        borderTop: "1px solid #E2E8F0",
        backgroundColor: "#ffffff",
        fontFamily: "Quicksand",
      }}
    >
      {/* Rows per page selector */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Typography
          sx={{
            fontSize: "14px",
            color: "#4A5568",
            fontFamily: "Quicksand",
            fontWeight: 500,
          }}
        >
          Rows per page:
        </Typography>
        <Select
          value={itemsPerPage}
          onChange={(e) => onItemsPerPageChange(Number(e.target.value))}
          sx={{
            height: "32px",
            fontSize: "14px",
            fontFamily: "Quicksand",
            "& .MuiOutlinedInput-notchedOutline": {
              border: "none",
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              border: "none",
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              border: "none",
            },
          }}
        >
          <MenuItem value={5} sx={{ fontFamily: "Quicksand", fontSize: "14px" }}>
            5
          </MenuItem>
          <MenuItem value={10} sx={{ fontFamily: "Quicksand", fontSize: "14px" }}>
            10
          </MenuItem>
          <MenuItem value={15} sx={{ fontFamily: "Quicksand", fontSize: "14px" }}>
            15
          </MenuItem>
          <MenuItem value={20} sx={{ fontFamily: "Quicksand", fontSize: "14px" }}>
            20
          </MenuItem>
        </Select>
      </Box>

      {/* Page info */}
      <Typography
        sx={{
          fontSize: "14px",
          color: "#4A5568",
          fontFamily: "Quicksand",
          fontWeight: 500,
        }}
      >
        {startItem}-{endItem} of {totalItems}
      </Typography>

      {/* Navigation buttons */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <IconButton
          onClick={handlePrevious}
          disabled={currentPage === 1}
          sx={{
            width: "32px",
            height: "32px",
            color: currentPage === 1 ? "#CBD5E0" : "#4A5568",
            "&:hover": {
              backgroundColor: "#F7FAFC",
            },
            "&.Mui-disabled": {
              color: "#CBD5E0",
            },
          }}
        >
          <ChevronLeftIcon fontSize="small" />
        </IconButton>
        <IconButton
          onClick={handleNext}
          disabled={currentPage === totalPages}
          sx={{
            width: "32px",
            height: "32px",
            color: currentPage === totalPages ? "#CBD5E0" : "#4A5568",
            "&:hover": {
              backgroundColor: "#F7FAFC",
            },
            "&.Mui-disabled": {
              color: "#CBD5E0",
            },
          }}
        >
          <ChevronRightIcon fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  );
};
